import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useMsal } from '@azure/msal-react';
import {
  MarketCategoriesError,
  type MarketCategoriesService,
  type MarketCategory,
  type TierThresholds,
} from '../services/marketCategoriesService';
import { ApiService } from '../services/apiService';
import { HttpMarketCategoriesService } from '../services/httpMarketCategoriesService';
import { getFriendlyApiErrorMessage, logApiError } from '../services/apiErrorMessages';

interface MarketCategoriesContextValue {
  categories: MarketCategory[];
  loading: boolean;
  loadError: string | null;
  addCategory: (input: { name: string }) => Promise<MarketCategory>;
  updateName: (id: string, name: string) => Promise<MarketCategory>;
  updateCountries: (id: string, countries: string[]) => Promise<MarketCategory>;
  updateThresholds: (id: string, thresholds: TierThresholds) => Promise<MarketCategory>;
  removeCategory: (id: string) => Promise<void>;
  cancelPendingChange: (id: string) => Promise<void>;
  cancelPendingThresholds: (id: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const MarketCategoriesContext = createContext<MarketCategoriesContextValue | undefined>(
  undefined,
);

interface ProviderProps {
  children: React.ReactNode;
  service?: MarketCategoriesService;
}

export const MarketCategoriesProvider: React.FC<ProviderProps> = ({
  children,
  service: serviceOverride,
}) => {
  const { instance, accounts } = useMsal();
  const account = instance.getActiveAccount() ?? accounts[0] ?? null;

  // Build the HTTP-backed service once per (instance, account) pair. Tests
  // can bypass MSAL by passing a `service` prop (e.g. the in-memory impl).
  const httpService = useMemo<MarketCategoriesService | null>(() => {
    if (serviceOverride || !account) return null;
    const api = new ApiService(instance, account);
    return new HttpMarketCategoriesService(api);
  }, [serviceOverride, instance, account]);

  const service = serviceOverride ?? httpService;

  const [categories, setCategories] = useState<MarketCategory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!service) {
      setCategories([]);
      setLoading(false);
      setLoadError('No authenticated account found.');
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const data = await service.list();
      setCategories(data);
    } catch (err) {
      logApiError('MarketCategoriesContext.refresh', err);
      setLoadError(getFriendlyApiErrorMessage(err, 'Failed to load categories.'));
    } finally {
      setLoading(false);
    }
  }, [service]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const requireService = useCallback((): MarketCategoriesService => {
    if (!service) {
      throw new MarketCategoriesError(
        'NOT_FOUND',
        'No authenticated account; cannot mutate market categories.',
      );
    }
    return service;
  }, [service]);

  const addCategory = useCallback<MarketCategoriesContextValue['addCategory']>(
    async (input) => {
      const created = await requireService().create(input);
      setLoadError(null);
      return created;
    },
    [requireService],
  );

  const updateName = useCallback<MarketCategoriesContextValue['updateName']>(
    async (id, name) => {
      const updated = await requireService().updateName(id, name);
      setCategories((prev) => prev.map((c) => (c.id === id ? updated : c)));
      setLoadError(null);
      return updated;
    },
    [requireService],
  );

  const updateCountries = useCallback<MarketCategoriesContextValue['updateCountries']>(
    async (id, countries) => {
      const updated = await requireService().updateCountries(id, countries);
      setCategories((prev) => prev.map((c) => (c.id === id ? updated : c)));
      setLoadError(null);
      return updated;
    },
    [requireService],
  );

  const updateThresholds = useCallback<MarketCategoriesContextValue['updateThresholds']>(
    async (id, thresholds) => {
      const updated = await requireService().updateThresholds(id, thresholds);
      setCategories((prev) => prev.map((c) => (c.id === id ? updated : c)));
      setLoadError(null);
      return updated;
    },
    [requireService],
  );

  const removeCategory = useCallback<MarketCategoriesContextValue['removeCategory']>(
    async (id) => {
      await requireService().remove(id);
      setCategories((prev) => prev.filter((c) => c.id !== id));
      setLoadError(null);
    },
    [requireService],
  );

  const cancelPendingChange = useCallback<MarketCategoriesContextValue['cancelPendingChange']>(
    async (id) => {
      await requireService().cancelPending(id);
      setLoadError(null);
    },
    [requireService],
  );

  const cancelPendingThresholds = useCallback<MarketCategoriesContextValue['cancelPendingThresholds']>(
    async (id) => {
      await requireService().cancelPendingThresholds(id);
      setLoadError(null);
    },
    [requireService],
  );

  const value = useMemo<MarketCategoriesContextValue>(
    () => ({
      categories,
      loading,
      loadError,
      addCategory,
      updateName,
      updateCountries,
      updateThresholds,
      removeCategory,
      cancelPendingChange,
      cancelPendingThresholds,
      refresh,
    }),
    [categories, loading, loadError, addCategory, updateName, updateCountries, updateThresholds, removeCategory, cancelPendingChange, cancelPendingThresholds, refresh],
  );

  return (
    <MarketCategoriesContext.Provider value={value}>{children}</MarketCategoriesContext.Provider>
  );
};

export const useMarketCategories = (): MarketCategoriesContextValue => {
  const ctx = useContext(MarketCategoriesContext);
  if (!ctx) {
    throw new Error('useMarketCategories must be used within a MarketCategoriesProvider');
  }
  return ctx;
};

export { MarketCategoriesError };
